import { createFileRoute } from '@tanstack/react-router';

import { AdminPageContent } from '@/features/admin/components/AdminPageContent';
import {
  AdminPageHeader,
  BreadcrumbItemProps,
} from '@/features/admin/components/AdminPageHeader';
import { DocumentDetail } from '@/features/case-list/components/DocumentDetail';
import {
  getPackageWithGroupsQueryOptions,
  useGetPackageWithGroups,
} from '@/features/case-list/queries/getPackage';
import { UserRoles } from '@/lib/constants/auth.constants.ts';
import { requireRole } from '@/lib/validation';

// Dynamic breadcrumbs that will include the document title once loaded
export const Route = createFileRoute('/admin/case-standards/$documentId')({
  component: DocumentDetailComponent,
  loader: async ({ context, params }) => {
    const { documentId } = params;
    await context.queryClient.prefetchQuery(
      getPackageWithGroupsQueryOptions(documentId)
    );
  },
  beforeLoad: requireRole([UserRoles.ADMINISTRATOR]),
  ssr: false,
  caseSensitive: true,
});

function DocumentDetailComponent() {
  const { documentId } = Route.useParams();
  const { data, isLoading, error } = useGetPackageWithGroups(documentId);

  // Create breadcrumb items with document title if available
  const breadcrumbItems: BreadcrumbItemProps[] = [
    {
      label: 'Admin',
      link: { to: '/admin' },
    },
    {
      label: 'Case Standards',
      link: { to: '/admin/case-standards' },
    },
  ];

  // Add document title to breadcrumbs if available
  if (data?.CFPackageWithGroups) {
    breadcrumbItems.push({
      label: data.CFPackageWithGroups.CFDocument.title,
      link: { to: '/admin/case-standards/$documentId', params: { documentId } },
    });
  }

  if (error) {
    return (
      <AdminPageContent>
        <AdminPageHeader breadcrumbItems={breadcrumbItems} />
        <div className="p-4 text-red-600">
          <h2 className="text-xl font-bold mb-2">Error Loading Document</h2>
          <p>{error.message || 'An unknown error occurred'}</p>
        </div>
      </AdminPageContent>
    );
  }

  return (
    <AdminPageContent>
      <AdminPageHeader breadcrumbItems={breadcrumbItems} />
      <div className="p-4">
        {data?.CFPackageWithGroups ? (
          <DocumentDetail
            packageData={data.CFPackageWithGroups}
            isLoading={isLoading}
          />
        ) : isLoading ? (
          <div className="flex justify-center items-center h-64">
            <p className="text-muted-foreground">Loading document details...</p>
          </div>
        ) : (
          <div className="flex justify-center items-center h-64">
            <p className="text-red-500">Document not found</p>
          </div>
        )}
      </div>
    </AdminPageContent>
  );
}
